import { useState, useEffect, useCallback } from "react";
import { Container, Row, Col } from "react-grid-system";
import { 
    EntityType, 
    FormMode, 
    DATE_FORMAT, 
    RoutePath, 
    getDriverFileInputConfig,
    IFileInputConfig, 
    DsLayout,DocumentType,
    AppUserRole,
    IAppUserProfile,
    ULTRA_LATE_DATE,
    ResponseStatus,
    ResponseCode
} from "shared/constants";
import { InputAttach, DatePicker, Button, Select} from "@ds/ds-snam/react";
import { ISelectLabel } from "@ds/ds-snam/dist/models/select";
import { StepperType } from "views/contractView/components/newElementFormStepper";
import Stepper from "views/contractView/components/newElementFormStepper";
import "views/contractView/styles/newElementFormStyles.css";
import { useDispatch, useSelector } from "react-redux";
import { requestPending, requestPristine } from "state/reducer/requestStatusSlice";
import { downloadDocument, getTransporterDomain, postDriver, putDriver } from "net/contracts";
import { IDriver } from "dataModels/contract";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useLoaderData } from "react-router-dom";
import DriverFormDataInput from "./driverFormDataInput";
import moment from "moment";
import { DSInput, DSFile, DSSelectList } from "@ds/ds-snam/dist/index";
import DSFileInput from "components/DSFileInput";
import { FileStatusEnum, IFileUpload } from "dataModels/fileInput";
import { IDocument } from "dataModels/contract";
import _ from "lodash";
import DriverFormReview from "./driverFormReview";
import { INotificationAction, NotificationsStatusType } from "dataModels/stateModels/notification";
import { hideNotification, showNotification } from "state/reducer/notificationSlice";
import SystemError from "components/SystemError";
import { v4 as uuid } from "uuid";
import { dsSelectHandler } from "shared/utils/dsComponentUtils";
import { RootState } from "state/appStore";
import saveAs from "file-saver";
import useAppUser from "hooks/useAppUser";
import { AppUser } from "ooAuth2/appUser";


const DriverForm: React.FC = (): JSX.Element => {
    //Driver id - When access to retrieve and edit driver existing information, otherwise undefined
    const { id } = useParams();
    //Loaded driver data when in edit mode
    const driverData: IDriver | undefined = useLoaderData() as IDriver;


    const { t } = useTranslation(['contract', 'app']);
    const navigate = useNavigate();
    const getDefaultFormData = (): IDriver => {
        const defaultData: IDriver = {
            driverId: "", // identificativo dell’autista
            transporterId: "", // identificativo dell’azienda trasportatrice a cui l’autista appartiene
            firstName: "", // nome
            lastName: "", // cognome
            birthPlace: "", // luogo di nascita
            birthDate: Math.floor(Date.now() / 1000), //data di nascita
            address: "", // indirizzo
            streetNumber: "", // numero civico
            city: "", // città
            postalCode: "", // codice postale
            province: "", // provincia
            state: "", // stato(es.IT)
            documentNumber: "", // numero del documento di riconoscimento
            documentType: "", // documento di riconoscimento(es: ci, patente, passaporto)
            documents: Array(7).fill({
                type: '',
                name: '',
                size: 0,
                content: '',
                expire: null
            }),
            status: 5, 
            statusComplete:5,
            cardCode:''
        }
        return defaultData;
    }
    //eslint-disable-next-line
    const [formMode, setFormMode] = useState<number>(id ? FormMode.EDIT : FormMode.NEW);
    const [formStep, setFormStep] = useState<number>(formMode === FormMode.NEW ? 0 : 1);
    const [transporterList, setTransporterList] = useState<ISelectLabel[]>([]);
    //const [selectedTransporter, setSelectedTransporter] = useState<ISelectLabel | undefined>();
    const [formData, setFormData] = useState<IDriver>(formMode === FormMode.NEW ? getDefaultFormData() : driverData);
    const [formError, setFormError] = useState<boolean>(false);
    const [cardCodeError, setCardCodeError] = useState<boolean>(false);
    const layoutMode = useSelector((state:RootState)=>{
        return state.dsLayout.mode;
    })
    const dispatch = useDispatch();
    const appUser: AppUser | undefined = useAppUser();
    const appUserRole: AppUserRole | undefined = appUser?.getRole();
    const appUserProfile: IAppUserProfile | undefined = appUser?.userProfile;

    const loadTransporters = useCallback(() => {
        dispatch(requestPending());
        getTransporterDomain(EntityType.TRANSPORTER)
            .then((response: ISelectLabel[]) => {
                setTransporterList(response)
            })
            .catch((error) => {
                console.log(error)
            })
            .finally(() => {
                dispatch(requestPristine())
            })
    }, [dispatch]);

    useEffect(() => {
        if (formMode === FormMode.NEW) {
            //Selector for transporter available only in new form mode
            loadTransporters();
        }
        //eslint-disable-next-line
    }, [loadTransporters]);

    const prevStep = () => {
        const nextStep = formStep - 1;
        setFormStep(nextStep);
    }
    const nextStep = () => {
        const nextStep = formStep + 1;
        setFormStep(nextStep);
    }

    const onTextInputChange = (inputEvent: Event) => {
        const fieldName = (inputEvent as CustomEvent).detail.input.name;
        const inputValue = (inputEvent as CustomEvent).detail.input.value;
        let currentData: IDriver = formData;
        (currentData[fieldName as keyof IDriver] as string) = inputValue;
        setFormData(currentData);

        if (fieldName === 'cardCode') {
            setCardCodeError(inputValue.length > 8);
        }
    }
    const onDateInputChange = (inputEvent: Event, fieldName: string) => {
        const date: string = (inputEvent as CustomEvent).detail.dateSelection;
        formData.birthDate = moment.utc(date, DATE_FORMAT).unix();
    }
    const downloadPrivacyPolicy = () => {
        downloadDocument("-1", EntityType.PRIVACYPOLICY, DocumentType.PRIVACYPOLICY)
            .then((response) => {
                let file = new File([response.data],"PrivacyPolicy.pdf");
                saveAs(file);
            })
            .catch((error) => {
                console.log(error);
            });
    }
    const fileInputConfig: IFileInputConfig[] = getDriverFileInputConfig();

    const getFileInputElements = () => {
        //there's a bug on ds component if I filter array, better to reander them all and set display noine with css
        //return _.filter(fileInputConfig, { 'step': formStep }).map((fileConfigObj) => { 
        return fileInputConfig.map((fileConfigObj: IFileInputConfig) => {
            return (<div key={uuid()} >
                <Row>
                    <Col>
                        <div className="fileInputWrapper" style={{ display: formStep === fileConfigObj.step ? "block" : "none", marginTop:'12px' }}>
                            <DSFileInput
                                title={
                                    t(
                                        'drivers.driverForm.sections.fileInput.title.' + 
                                        fileConfigObj.id
                                    ) +
                                    (!(fileConfigObj.id === 0 || fileConfigObj.id === 1) ? "*" : "")
                                }
                                inputLabel={formData.documents[fileConfigObj.id].content.length ? t('dsComponents.fileInput.uploadedStatus.title', { ns: 'app' }) : t('dsComponents.fileInput.defaultStatus.title', { ns: 'app' })}
                                buttonText={t('dsComponents.fileInput.defaultStatus.subTitle', { ns: 'app' })}
                                id={"doc" + fileConfigObj.id}
                                accept="application/pdf, image/jpg, image/jpeg"
                                maxSize={10000000}
                                defaultStatus={formData.documents[fileConfigObj.id].content.length > 0 ? FileStatusEnum.UPLOADED : FileStatusEnum.DEFAULT}
                                onFileUpload={(fileUpload: IFileUpload, id: string) => {
                                    let currentFormData = { ...formData };
                                    let base64String = btoa(fileUpload.content);
                                    const documentObj: IDocument = {
                                        type: fileConfigObj.documentType,
                                        name: fileUpload.name,
                                        size: fileUpload.size,
                                        content: base64String,
                                        expire: fileConfigObj.expireRequired ? Math.floor(Date.now() / 1000) : null
                                    }
                                    currentFormData.documents[fileConfigObj.id] = documentObj;
                                    setFormData(currentFormData);
                                }}
                                documentType={fileConfigObj.documentType}
                                action1={downloadPrivacyPolicy}
                            />
                        </div>
                    </Col>
                </Row>

                {formData.documents[fileConfigObj.id].content.length > 0 && fileConfigObj.step === formStep && <Row>
                    <div className="uploadedFileWrapper" style={{paddingLeft:'16px'}}>
                        <InputAttach
                            attachName={formData.documents[fileConfigObj.id].name}
                            firstButtonDisabled={false}
                            secondButtonDisabled={false}
                            inputLabel={t('contract.contractForm.sections.fileInput.attachTitle')}//"File caricato"
                        />
                    </div>

                    {formData.documents[fileConfigObj.id].content.length && fileConfigObj.expireRequired && fileConfigObj.step === formStep &&
                        <Col xs={12} md={3}>
                            <div className="expirationInputWrapper" style={{/*width:'196px'*/}}>
                                <DatePicker
                                    id={"scadenzaDoc" + fileConfigObj.id}
                                    datePickerTitle={t('transporter.transporterForm.sections.fileInput.datePickerTitle') + "*"}//"Scadenza*"
                                    datePickerVariant="full-date"
                                    startingYear={new Date().getFullYear()}
                                    endingYear={2099}
                                    defaultDate= {new Date(formData.documents[fileConfigObj.id].expire!*1000)} //{new Date()}
                                    disabledDates={{ lastDisabledDate: new Date(Date.now() - 86400000) }} 
                                    isDisabled={false}
                                    datePickerStatus="default"
                                    confirmButtonLabel={t('general.buttons.confirm', { ns: 'app' })}//"Conferma"
                                    hasCloseButton={false}
                                    zIndex={999}
                                    isReadonly={false}
                                    isPanelTop={true}
                                    onDateSelection={(event: Event) => {
                                        const date: string = (event as CustomEvent).detail.dateSelection;
                                        /*let currentFormData = { ...formData };
                                        currentFormData.documents[fileConfigObj.id].expire = moment(date, DATE_FORMAT).unix();
                                        setFormData(currentFormData);*/
                                        formData.documents[fileConfigObj.id].expire = moment.utc(date, DATE_FORMAT).unix();
                                    }}
                                ></DatePicker>
                            </div>
                        </Col>}
                </Row>}
            </div>)
        });
    }

    const validateStep = (): number => {
        let errors: number = 0;
        if (formStep === 1) {
            //console.log(formStep)
            Object.keys(formData).forEach((key: string) => {
                if (key !== 'cardCode') {  // Esclude identityCard dal controllo
                    const elem: DSInput | null = document.querySelector("[input-id='" + key + "']");
                    if (elem) {
                        const content = formData[key as keyof IDriver]!;
                        const koCheck: boolean = content === "" || Number(content) <= 0;
                        elem.inputStatus = koCheck ? 'error' : 'default';
                        if (koCheck)
                            errors++;
                    }
                }
            });
                const cardCodeField: DSInput | null = document.querySelector("[input-id='cardCode']");
                if (cardCodeField) {
                    const isCardCodeTooLong = formData.cardCode.length > 8;
                    setCardCodeError(isCardCodeTooLong);
                    cardCodeField.inputStatus = isCardCodeTooLong ? 'error' : 'default';
                    if (isCardCodeTooLong) errors++;
                }
                // Controllo per i campi Select (provincia e nazione)
                const provinceField: DSSelectList | null = document.querySelector("[id='id-select-province']");
                const nationField: DSSelectList | null = document.querySelector("[id='id-select-nation']");
                const docTypeField: DSSelectList | null = document.querySelector("[id='id-select-documentType']");    
                if (provinceField) {
                    const isProvinceEmpty = formData.province === "";
                    provinceField.selectStatus = isProvinceEmpty ? "error" : "default";
                    if (isProvinceEmpty) errors++;
                }
                        
                if (nationField) {
                    const isNationEmpty = formData.state === "";
                    nationField.selectStatus = isNationEmpty ? "error" : "default";
                    if (isNationEmpty) errors++;
                }
                if (docTypeField) {
                    const isDocTypeEmpty = formData.documentType === "";
                    docTypeField.selectStatus = isDocTypeEmpty ? "error" : "default";
                    if (isDocTypeEmpty) errors++;
                }
                        
        } else if (formStep === 2 || formStep === 3) {
            _.filter(fileInputConfig, { 'step': formStep }).forEach((configObj) => {
                const docId: string = 'doc' + configObj.id;
                const elem: DSFile | null = document.querySelector("[input-id='" + docId + "']");
                /**
                doc0 ID //FACOLTATIVO
                doc1 Fiscal code //FACOLTATIVO
                doc2 Driver licence *
                doc3 Agreement for personal data *
                doc4 CPC *
                doc5 ADR *
                doc6 Training and information *
                 */
                //Non piu obbligatori:  
                //console.log(docId, elem);
                const koCheck: boolean = formData.documents[configObj.id].content.length === 0 && (docId!=='doc1'&&docId!=='doc0'); 
                
                if (koCheck)
                    errors++;
                if (elem && koCheck)
                    elem.inputStatus = FileStatusEnum.ERROR;//koCheck ? FileStatusEnum.ERROR : (formData.documents[configObj.id].content.length === 0?FileStatusEnum.DEFAULT:FileStatusEnum.UPLOADED);
            });
        }
        return errors;
    }

    const saveFormData = useCallback(() => {
        const translationField: string = formMode === FormMode.NEW ? "new" : "edit";
        const request = formMode === FormMode.EDIT ? putDriver : postDriver;
        let popUpAction: INotificationAction;
        dispatch(requestPending());

        const defaults:any = {
                0: {
                    type: DocumentType.IDENTITY_CARD,
                    name: DocumentType.EMPTY_FILE,
                    size: 0,
                    content: '',
                    expire: ULTRA_LATE_DATE
                },
                1: {
                    type: DocumentType.FISCAL_CODE,
                    name: DocumentType.EMPTY_FILE,
                    size: 0,
                    content: '',
                    expire: ULTRA_LATE_DATE
                }
            };
        //relativo al fiscalCode [1]
        formData.documents = formData.documents.map((doc, index) => {
            // Se ha già un type (quindi non è vuoto), lo lascio invariato
            if (doc.type && doc.type.trim() !== '') return doc;
            // Se l'indice ha un default, uso quello
            if (defaults[index]) return defaults[index];
            // Altrimenti lascio com'è
            return doc;
        });

        request(formData)
            .then((response) => {
                let popUpAction: INotificationAction;
                const popUpTitle:string = response.data.status===ResponseStatus.ERROR ? 'Error':(response.data.code===ResponseCode.UNPROCESSABLE_CONTENT? '422': t(`drivers.driverForm.successToast.${translationField}.title`) );
                const popUpDescription:string = response.data.status===ResponseStatus.ERROR||response.data.code===ResponseCode.UNPROCESSABLE_CONTENT?response.data.message:t(`drivers.driverForm.successToast.${translationField}.subTitle`);
                const popUpStatus:NotificationsStatusType = response.data.status===ResponseStatus.ERROR?"error":(response.data.code===ResponseCode.UNPROCESSABLE_CONTENT?"warning":"confirm");
                popUpAction = {
                    show: true,
                    title: popUpTitle,
                    description: popUpDescription,
                    status: popUpStatus,
                    onClose: (event: Event) => {
                        dispatch(hideNotification());
                    }
                };
                dispatch(showNotification(popUpAction));
                if(formMode===FormMode.NEW){
                    navigate(RoutePath.CONTRACTS_DRIVER_INFO);
                }else{ //edit
                    navigate(RoutePath.CONTRACTS_DRIVER_INFO+"/"+id);
                }
            })
            .catch((error) => {
                console.log(error);
                /*popUpAction = {
                    show:true,
                    title: "Non è possibile completare l’azione",
                    description: "Si è verificato un errore durante l’inserimento del contratto. Riprova a caricare il file o contatta il nostro team di supporto.",
                    status: "error",
                    onClose: (event:Event)=>{
                        dispatch(hideNotification());
                    }
                }*/
                setFormError(true);//error step
            })
            .finally(() => {
                dispatch(requestPristine());
            })
        //eslint-disable-next-line
    }, [formData, dispatch, navigate, t]);

    const headers: string[] = (() => {
        const mode: string = formMode === FormMode.NEW ? "new" : "edit";
        return t("drivers.driverForm.pageHeaders." + mode, { returnObjects: true }) as string[];
    })();

    const onSelectInputChange = (event: Event,fieldName:String) => {
        const selection: ISelectLabel = (event as CustomEvent).detail.option;
        /*let currentFormData = { ...formData };
        (currentFormData[fieldName as keyof ICustomer] as string) = selection?.value!;
        setFormData(currentFormData);*/

        setFormData(prevFormData => ({
            ...prevFormData,
            [fieldName as keyof IDriver]: selection?.value || "" // Aggiorna il campo specificato con il nuovo valore
        }));
    }

    return <>
        <p className="pageHeader">
        {headers[formStep < 4 ? 0 : 1]}
        </p>
        {/*<Container fluid>*/}
            {formError ?
                <SystemError link={RoutePath.CONTRACTS_DRIVER_INFO} />
                : <>
                    {/** Stepper - insert mode only */}
                    {formMode === FormMode.NEW && formStep > 0 && <Row>
                        <Col>
                            <Stepper
                                stepperType={StepperType.NEW_DRIVER}
                                currentStep={formStep}
                                finalStep={4} />
                        </Col>
                    </Row>}

                    {/** Transporter selector + insert transport button - insert mode only */}
                    {formStep === 0 && <Row>
                        <Col xs={4}>
                            <div className="textInputWrapper">
                                <Select
                                    id="transporterSelector"
                                    menuPlaceholder=""
                                    hasIcon={false}
                                    menuOptions={transporterList}
                                    menuLabel={t('drivers.driverForm.sections.selector.title')}//"Scegli il trasportatore di riferimento"
                                    menuId="select-id"
                                    isDisabled={false}
                                    selectStatus="default"
                                    onOptionSelection={(event: Event) => {
                                        //console.log((event as CustomEvent).detail.option)
                                        const transporter: ISelectLabel = (event as CustomEvent).detail.option;
                                        dsSelectHandler(transporterList, transporter);
                                        let currentFormData: IDriver = { ...formData };
                                        currentFormData.transporterId = transporter.value!;
                                        setFormData(currentFormData);
                                    }}

                                ></Select>
                            </div>
                            {/** Pulsante inserimento trasportatore */}
                            {appUserProfile?.addEntityPolicy.includes(EntityType.TRANSPORTER) &&
                                <Button
                                    buttonType="button"
                                    buttonUIVariant="secondary"
                                    buttonSize="medium"
                                    buttonLabel={t('general.buttons.addElement', { ns: 'app', what: t('transporter.transporterList.addButton') })}//{"Inserisci trasportatore"}
                                    isDisabled={false}
                                    buttonAriaLabel="Inserisci trasportatore"
                                    buttonIcon="icon-add"
                                    onClickEvent={(event: Event) => {
                                        navigate(RoutePath.CONTRACTS_NEW_TRANSPORT);
                                    }}
                                ></Button>
                            }

                        </Col>
                    </Row >}

                    {/** Step 1 - Data input */}
                    {formStep === 1 && <div className="listTableContainerGrey">
                        <Row>
                            <Col>
                                <p className="formSubTitle">
                                    {/*Inserisci le informazioni dell'autista*/}
                                    {t('drivers.driverForm.sections.dataInput.title')}
                                </p>
                            </Col>
                        </Row>

                        <DriverFormDataInput
                            formData={formData}
                            cardCodeError={cardCodeError}
                            onTextInputChange={onTextInputChange}
                            onDateInputChange={onDateInputChange}
                            onSelectInputChange={onSelectInputChange}
                        />
                    </div>}

                    {/** Step 2-3 files input */}
                    {(formStep === 2 || formStep === 3) && <div className="listTableContainerGrey">
                        <Row>
                            <Col>
                                <p className="formSubTitle">
                                    {/*Inserisci la documentazione relativa all'autista*/}
                                    {t('drivers.driverForm.sections.fileInput.sectionTitle')}
                                </p>
                            </Col>
                        </Row>
                        {getFileInputElements()}
                    </div>}

                    {/** Step 4 - review */}
                    {formStep === 4 &&
                        <DriverFormReview formData={formData} formMode={formMode} />
                    }

                </>}
        {/*</Container >*/}

        {/** Buttons */}
        {!formError && <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "0.8rem" }}>
            {formStep > 0 && <Button
                style={{ marginRight: "0.8rem" }}
                id="backButton"
                buttonType="button"
                buttonUIVariant="secondary"
                buttonSize="medium"
                buttonLabel={t('general.buttons.back', { ns: 'app' })/*formMode === FormMode.NEW ? t('general.buttons.back', { ns: 'app' }) : t('general.buttons.cancel', { ns: 'app' })*/}//"Indietro"
                buttonIcon=""
                isDisabled={false}
                onClickEvent={() => {
                    formMode === FormMode.NEW ? prevStep() : (formStep === 4 ? setFormStep(1) : navigate(RoutePath.CONTRACTS_DRIVER_INFO + "/" + id));
                }}
            />}
            {formStep < 4 /*&& ((formMode === FormMode.NEW && formData.transporterId) || formMode === FormMode.EDIT)*/ && <Button
                id="nextButton"
                buttonType="button"
                buttonUIVariant="primary"
                buttonSize="medium"
                buttonLabel={t('general.buttons.next', { ns: 'app' })}//"Continua"
                buttonIcon=""
                isDisabled={!((formMode === FormMode.NEW && formData.transporterId) || formMode === FormMode.EDIT)}
                onClickEvent={() => {
                    let errors: number = validateStep();
                    if (errors === 0) {
                        formMode === FormMode.NEW ? nextStep() : setFormStep(4);
                    }
                }}
            />}
            {formStep === 4 && <Button
                buttonType="button"
                buttonUIVariant="primary"
                buttonSize="medium"
                buttonLabel={t('general.buttons.confirm', { ns: 'app' })}//"Conferma"
                buttonIcon=""
                isDisabled={false}
                onClickEvent={(event: Event) => {
                    //console.log(formData)
                    saveFormData();
                }}
            />}
        </div>}
    </>
}
export default DriverForm;